import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { GasPlantCapacitiesService, GasPlantCapacityRow, GasPlantCapacitiesDetailRequest, FilterColumn } from '../../services/gas-plant-capacities.service';
import { TableColumn } from '../../services/interfaces/gas-plant-capacities.interfaces';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-gas-plant-capacities',
  standalone: false,
  templateUrl: './gas-plant-capacities.component.html',
  styleUrls: ['./gas-plant-capacities.component.scss']
})
export class GasPlantCapacitiesComponent implements OnInit, OnDestroy {
  private readonly blankFilterToken = 'NULL';
  private readonly gasPlantFacilityType = 'gas plant';
  private readonly gasCapacityType = 'Gas (mcf/d)';

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: GasPlantCapacityRow[] = [];
  selectedRows: GasPlantCapacityRow[] = [];
  showCancel: boolean = false;
  loading: boolean = false;
  hoveredRowIndex: number | null = null;

  // Pagination tracking
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;

  // Global dropdown options
  facilityTypeOptions: string[] = [];
  developmentAreaOptions: string[] = [];
  facilityNameOptions: string[] = [];
  gasTakeAwayOptions: string[] = [];
  oilTakeAwayOptions: string[] = [];
  capacityTypeOptions: string[] = ['Gas (mcf/d)', 'Oil (bbl/d)'];

  // Per-row filtered dropdown options
  facilityTypeOptionsMap: { [rowId: string]: string[] } = {};
  developmentAreaOptionsMap: { [rowId: string]: string[] } = {};
  facilityNameOptionsMap: { [rowId: string]: string[] } = {};
  subDevelopmentAreaOptions: string[] = [];

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    assetType: null,
    developmentArea: null,
    facilityName: null,
    gasTakeAway: null,
    oilTakeAway: null,
    capacityType: null,
  };

  // Date range filters for effective date
  dateRangeFilters: {
    startDate: string | null;
    endDate: string | null;
  } = {
    startDate: null,
    endDate: null,
  };

  // Search filter for capacity
  capacitySearchFilter: string | null = null;

  public dialogMessage: string = '';
  private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'assetType', header: 'Facility Type', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'developmentArea', header: 'Development Area', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'facilityName', header: 'Facility Name', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'gasTakeAway', header: 'Gas Take Away', required: false, filterable: true, filterType: 'multi-select',locked: true },
    { field: 'oilTakeAway', header: 'Oil Take Away', required: false, filterable: true, filterType: 'multi-select',locked: true },
    { field: 'capacityType', header: 'Capacity Type', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'capacity', header: 'Capacity', required: true, filterable: true, filterType: 'search' },
    { field: 'effective', header: 'Effective Date', required: true, filterable: true, filterType: 'date-range' },
    { field: 'comment', header: 'Comment', required: false, filterable: false }
  ];

  constructor(
    private toastr: ToastrService,
    private gasPlantService: GasPlantCapacitiesService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadFirstPage();
    await this.loadInitialDropdowns();
    await this.loadFilterOptionsInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      const dropdownData = await this.gasPlantService.getAllDropdownOptions();
      
      this.facilityTypeOptions = dropdownData.facilityTypes || [];
      this.developmentAreaOptions = dropdownData.developmentAreas || [];
      this.subDevelopmentAreaOptions = dropdownData.SubDevelopmentArea || [];
      this.facilityNameOptions = dropdownData.facilityNames || [];
      this.gasTakeAwayOptions = this.normalizeFilterOptions(dropdownData.gasTakeAways);
      this.oilTakeAwayOptions = this.normalizeFilterOptions(dropdownData.oilTakeAways);

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async loadFilterOptionsInit(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const response = await this.gasPlantService.getCascadingFilterOptions({
        developmentArea: null,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        capacityType: null
      });

      if (response && response.length > 0) {
        response.forEach((column: FilterColumn) => {
          const fieldName = this.mapColumnNameToFieldName(column.columnName);
          this.filterColumnsData[fieldName] = this.normalizeFilterOptions(column.values);
        });

      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const response = await this.gasPlantService.getCascadingFilterOptions({
        developmentArea: this.currentFilters['developmentArea'] || null,
        facilityName: this.currentFilters['facilityName'] || null,
        facilityType: this.currentFilters['assetType'] || null,
        gasTakeAway: this.currentFilters['gasTakeAway'] || null,
        oilTakeAway: this.currentFilters['oilTakeAway'] || null,
        capacityType: this.currentFilters['capacityType'] || null
      });

      if (response && response.length > 0) {
        response.forEach((column: FilterColumn) => {
          const fieldName = this.mapColumnNameToFieldName(column.columnName);
          this.filterColumnsData[fieldName] = this.normalizeFilterOptions(column.values);
        });

      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reload filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private mapColumnNameToFieldName(columnName: string): string {
    const columnMap: { [key: string]: string } = {
      'CapacityType': 'capacityType',
      'DevelopmentArea': 'developmentArea',
      'FacilityName': 'facilityName',
      'FacilityType': 'assetType',
      'GasTakeAway': 'gasTakeAway',
      'OilTakeAway': 'oilTakeAway'
    };

    return columnMap[columnName] || columnName;
  }

  private isBlankFilterValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    const normalized = String(value).trim();
    return normalized === '' || normalized.toLowerCase() === 'null';
  }

  private normalizeFilterValue(value: unknown): string {
    if (this.isBlankFilterValue(value)) {
      return this.blankFilterToken;
    }

    return String(value).trim();
  }

  private normalizeFilterOptions(values: unknown[] | undefined): string[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const normalizedValues = Array.from(
      new Set(values.map((value) => this.normalizeFilterValue(value)))
    );

    const blankIndex = normalizedValues.indexOf(this.blankFilterToken);
    if (blankIndex > 0) {
      normalizedValues.splice(blankIndex, 1);
      normalizedValues.unshift(this.blankFilterToken);
    }

    return normalizedValues;
  }

  async loadCapacities(filters?: Partial<GasPlantCapacitiesDetailRequest>): Promise<void> {
    try {
      this.spinnerBoolean = true;
      
      const filterPayload: GasPlantCapacitiesDetailRequest = {
        developmentArea: this.currentFilters['developmentArea'] || null,
        facilityName: this.currentFilters['facilityName'] || null,
        facilityType: this.currentFilters['assetType'] || null,
        gasTakeAway: this.currentFilters['gasTakeAway'] || null,
        oilTakeAway: this.currentFilters['oilTakeAway'] || null,
        capacityType: this.currentFilters['capacityType'] || null,
        startDate: this.dateRangeFilters.startDate || null,
        endDate: this.dateRangeFilters.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0,
        ...filters
      };

      const response = await this.gasPlantService.getCapacitiesWithPagination(filterPayload);
      
      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > 0;

      if (response.capacities && response.capacities.length > 0) {
        let newRows = response.capacities.map((capacity): GasPlantCapacityRow => ({
          _rowId: this.generateRowId(),
          developmentArea: capacity.developmentArea,
          developmentAreaCards: this.parseDevelopmentAreas(capacity.developmentArea),
          facilityName: capacity.facilityName,
          assetType: capacity.facilityType,
          gasTakeAway: (capacity as any).gasTakeAway ?? (capacity as any).gasTakeaway ?? '',
          oilTakeAway: (capacity as any).oilTakeAway ?? (capacity as any).oilTakeaway ?? '',
          capacityType: capacity.capacityType,
          capacity: capacity.capacity || null,
          effective: capacity.effectiveDate,
          comment: capacity.comment || '',
          isNew: false,
          _rowEdited: false,
          original: {}
        }));

        // Apply client-side capacity search filter if set
        if (this.capacitySearchFilter && this.capacitySearchFilter.trim() !== '') {
          const searchValue = this.capacitySearchFilter.toLowerCase().trim();
          newRows = newRows.filter(row => {
            const capacityStr = row.capacity?.toString().toLowerCase() || '';
            return capacityStr.includes(searchValue);
          });
        }

        for (const row of newRows) {
          row.original = { ...row };
          this.facilityTypeOptionsMap[row._rowId] = [...this.facilityTypeOptions];
          this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
          this.facilityNameOptionsMap[row._rowId] = [...this.facilityNameOptions];
        }

        if (filterPayload.lastRowNumber === 0) {
          this.data = newRows;
        } else {
          this.mergeRows(newRows);
        }

      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing capacity data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load capacities';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.spinnerBoolean = false;
      this.loading = false;
    }
  }

  private mergeRows(newRows: GasPlantCapacityRow[]): void {
    const keyOf = (r: GasPlantCapacityRow) =>
      `${r.developmentArea}||${r.facilityName}||${r.assetType}||${r.capacityType}||${r.effective}`;

    const existingKeys = new Set(this.data.map(keyOf));
    const toAdd = newRows.filter((r) => !existingKeys.has(keyOf(r)));
    this.data = [...this.data, ...toAdd];
  }

  loadFirstPage(): void {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    this.loadCapacities({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    const buffer = 10;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      index >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadCapacities({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  async onFacilityTypeChange(row: GasPlantCapacityRow, selectedType: string): Promise<void> {
    try {
      this.markAsEdited(row);
      
      if (!selectedType) {
        this.clearDevelopmentAreaSelection(row);
        this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
        this.facilityNameOptionsMap[row._rowId] = [...this.facilityNameOptions];
        return;
      }

      if (!row.isNew) {
        row.developmentArea = '';
        row.facilityName = '';
      }

      this.clearDevelopmentAreaSelection(row);
      this.applyGasPlantDefaults(row, selectedType);

      const [devAreas, facilities] = await Promise.all([
        this.gasPlantService.getDevelopmentAreasByFacilityType(selectedType),
        this.gasPlantService.getFacilityNamesByFacilityType(selectedType)
      ]);

      this.developmentAreaOptionsMap[row._rowId] = devAreas;
      this.facilityNameOptionsMap[row._rowId] = facilities;

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
      this.developmentAreaOptionsMap[row._rowId] = [];
      this.facilityNameOptionsMap[row._rowId] = [];
    }
  }

  async onDevelopmentAreaChange(row: GasPlantCapacityRow, selectedArea: string): Promise<void> {
    try {
      this.markAsEdited(row);
      
      if (!selectedArea) {
        if (row.assetType) {
          await this.onFacilityTypeChange(row, row.assetType);
        } else {
          this.facilityTypeOptionsMap[row._rowId] = [...this.facilityTypeOptions];
          this.facilityNameOptionsMap[row._rowId] = [...this.facilityNameOptions];
        }
        return;
      }

      if (!row.isNew) {
        row.facilityName = '';
      }

      if (row.assetType) {
        const [facilityTypes, facilities] = await Promise.all([
          this.gasPlantService.getFacilityTypesByDevelopmentArea(selectedArea),
          this.gasPlantService.getFacilitiesByDevAreaAndType(selectedArea, row.assetType)
        ]);
        
        this.facilityTypeOptionsMap[row._rowId] = facilityTypes;
        this.facilityNameOptionsMap[row._rowId] = facilities;
      } else {
        const [facilityTypes, facilities] = await Promise.all([
          this.gasPlantService.getFacilityTypesByDevelopmentArea(selectedArea),
          this.gasPlantService.getFacilityNamesByDevelopmentArea(selectedArea)
        ]);
        
        this.facilityTypeOptionsMap[row._rowId] = facilityTypes;
        this.facilityNameOptionsMap[row._rowId] = facilities;
      }

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
      this.facilityTypeOptionsMap[row._rowId] = [];
      this.facilityNameOptionsMap[row._rowId] = [];
    }
  }

  async onFacilityNameChange(row: GasPlantCapacityRow, selectedFacility: string): Promise<void> {
    try {
      this.markAsEdited(row);
      
      if (!selectedFacility) {
        if (this.isGasPlantRow(row)) {
          this.clearDevelopmentAreaSelection(row);

          if (row.assetType) {
            await this.onFacilityTypeChange(row, row.assetType);
          } else {
            this.facilityTypeOptionsMap[row._rowId] = [...this.facilityTypeOptions];
            this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
          }

          return;
        }

        if (row.assetType && row.developmentArea) {
          await this.onDevelopmentAreaChange(row, row.developmentArea);
        } else if (row.assetType) {
          await this.onFacilityTypeChange(row, row.assetType);
        } else if (row.developmentArea) {
          await this.onDevelopmentAreaChange(row, row.developmentArea);
        } else {
          this.facilityTypeOptionsMap[row._rowId] = [...this.facilityTypeOptions];
          this.developmentAreaOptionsMap[row._rowId] = [...this.developmentAreaOptions];
        }
        return;
      }

      const [facilityTypes, devAreas] = await Promise.all([
        this.gasPlantService.getFacilityTypesByFacilityName(selectedFacility),
        this.gasPlantService.getDevelopmentAreasByFacilityName(selectedFacility)
      ]);

      this.facilityTypeOptionsMap[row._rowId] = facilityTypes;
      this.developmentAreaOptionsMap[row._rowId] = devAreas;

      const resolvedFacilityType = facilityTypes.length === 1 ? facilityTypes[0] : row.assetType;
      this.applyGasPlantDefaults(row, resolvedFacilityType);

      if (facilityTypes.length === 1 && !row.assetType) {
        row.assetType = facilityTypes[0];
      }

      if (this.isGasPlantRow(row)) {
        this.updateDevelopmentAreaCards(row, devAreas);
      } else {
        row.developmentAreaCards = [];
      }

      if (!this.isGasPlantRow(row) && devAreas.length === 1 && !row.developmentArea) {
        row.developmentArea = devAreas[0];
      }

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
      this.facilityTypeOptionsMap[row._rowId] = [];
      this.developmentAreaOptionsMap[row._rowId] = [];
    }
  }

  onCommentChange(row: GasPlantCapacityRow, comment: string): void {
    row.comment = comment;
    this.markAsEdited(row);
  }

  getFacilityTypeOptionsForRow(row: GasPlantCapacityRow): string[] {
    return this.facilityTypeOptionsMap[row._rowId] || this.facilityTypeOptions;
  }

  getDevelopmentAreaOptionsForRow(row: GasPlantCapacityRow): string[] {
    return this.developmentAreaOptionsMap[row._rowId] || this.developmentAreaOptions;
  }

  getFacilityNameOptionsForRow(row: GasPlantCapacityRow): string[] {
    return this.facilityNameOptionsMap[row._rowId] || this.facilityNameOptions;
  }

  isGasPlantRow(row: GasPlantCapacityRow): boolean {
    return this.isGasPlantFacilityType(row.assetType);
  }

  getDevelopmentAreaCards(row: GasPlantCapacityRow): string[] {
    if (row.developmentAreaCards && row.developmentAreaCards.length > 0) {
      return row.developmentAreaCards;
    }

    return row.developmentArea ? [row.developmentArea] : [];
  }

  addRow(): void {
    const newRow: GasPlantCapacityRow = { 
      _rowId: this.generateRowId(), 
      developmentArea: '', 
      developmentAreaCards: [],
      facilityName: '', 
      assetType: '', 
      gasTakeAway: '',
      oilTakeAway: '',
      capacityType: '', 
      capacity: null, 
      effective: '', 
      comment: '',
      isNew: true, 
      _rowEdited: false, 
      original: {} 
    };
    
    this.facilityTypeOptionsMap[newRow._rowId] = [...this.facilityTypeOptions];
    this.developmentAreaOptionsMap[newRow._rowId] = [...this.developmentAreaOptions];
    this.facilityNameOptionsMap[newRow._rowId] = [...this.facilityNameOptions];
    
    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();
    
    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });
    
    this.toastr.info('New capacity row added. Click Cancel to discard or Save to keep changes.');
  }

  copyRow(index: number): void {
    const originalRow = this.data[index];
    if (!originalRow) return;

    const currentUser = this.authService.getUserName();

    const copiedRow: GasPlantCapacityRow = {
      _rowId: this.generateRowId(),
      developmentArea: originalRow.developmentArea,
      developmentAreaCards: originalRow.developmentAreaCards ? [...originalRow.developmentAreaCards] : [],
      facilityName: originalRow.facilityName,
      assetType: originalRow.assetType,
      gasTakeAway: originalRow.gasTakeAway || '',
      oilTakeAway: originalRow.oilTakeAway || '',
      capacityType: originalRow.capacityType,
      capacity: originalRow.capacity,
      effective: '',
      comment: originalRow.comment || '',
      isNew: true,
      _rowEdited: false,
      original: {}
    };

    this.facilityTypeOptionsMap[copiedRow._rowId] = [...this.facilityTypeOptions];
    this.developmentAreaOptionsMap[copiedRow._rowId] = [...this.developmentAreaOptions];
    this.facilityNameOptionsMap[copiedRow._rowId] = [...this.facilityNameOptions];

    this.data = [copiedRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    }, 100);

    this.toastr.info('Row copied successfully. Click Cancel to discard or Save to keep changes.');
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const required = this.columns.filter(col => col.required).map(col => col.field);
      const changedRows = this.data.filter(row => row.isNew || row._rowEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      const invalid = changedRows.filter(row => 
        required.some(field => {
          const value = row[field];
          return value === undefined || value === null || value === '';
        })
      );
      
      if (invalid.length) { 
        this.toastr.error('Please fill all required fields before saving.'); 
        return; 
      }

      this.spinnerBoolean = true;
      
      for (const row of changedRows) {
        const action = row.isNew ? 'INSERT' : 'UPDATE';
        const originalRow = row.original as GasPlantCapacityRow;
        
        const response = await this.gasPlantService.saveCapacity(row, action, userName, originalRow);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row._rowEdited = false;
          row.original = { ...row };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      await this.loadCapacities();
      this.reloadFilterOptions();

      if (successCount > 0) {
        this.toastr.success(`${successCount} capacity row(s) saved successfully`, 'Success');
        this.showCancel = false;
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} capacity row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save capacities', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) { 
      this.toastr.info('Please select rows to delete.'); 
      return; 
    }

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        if (row.isNew) {
          const index = this.data.indexOf(row);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          successCount++;
        } else {
          const response = await this.gasPlantService.deleteCapacity(row);
          const parsed = ErrorHandler.parseApiResult(response);

          if (parsed.success) {
            successCount++;
          } else {
            failureCount++;
            errorMessages.push(parsed.message || 'Unknown error occurred');
          }
        }
      }

      this.selectedRows.forEach(row => {
        delete this.facilityTypeOptionsMap[row._rowId];
        delete this.developmentAreaOptionsMap[row._rowId];
        delete this.facilityNameOptionsMap[row._rowId];
      });

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadCapacities();
      
      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete capacities', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    this.data = this.data.filter((row) => !row.isNew);
    
    this.data.forEach(row => {
      if (row._rowEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, row.original);
        row._rowEdited = false;
      }
    });
    
    this.showCancel = false;
    this.clearSelection();
    
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });
    
    this.toastr.info('Changes cancelled.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: GasPlantCapacityRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) { 
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row); 
    } else { 
      const index = this.selectedRows.indexOf(row); 
      if (index > -1) this.selectedRows.splice(index, 1); 
    }
  }

  markAsEdited(row: GasPlantCapacityRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row };
    }
    if (!row.isNew) {
      row._rowEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: GasPlantCapacityRow): void { 
    this.markAsEdited(row);
  }

  private isGasPlantFacilityType(facilityType: string | null | undefined): boolean {
    return (facilityType || '').trim().toLowerCase() === this.gasPlantFacilityType;
  }

  private applyGasPlantDefaults(row: GasPlantCapacityRow, facilityType: string | null | undefined): void {
    if (!this.isGasPlantFacilityType(facilityType)) {
      return;
    }

    row.oilTakeAway = '';
    row.capacityType = this.gasCapacityType;
  }

  private updateDevelopmentAreaCards(row: GasPlantCapacityRow, developmentAreas: string[]): void {
    const uniqueAreas = Array.from(new Set((developmentAreas || []).filter(Boolean)));
    row.developmentAreaCards = uniqueAreas;
    row.developmentArea = uniqueAreas[0] || '';
  }

  private parseDevelopmentAreas(developmentArea: string | null | undefined): string[] {
    return Array.from(
      new Set(
        (developmentArea || '')
          .split(',')
          .map(area => area.trim())
          .filter(Boolean)
      )
    );
  }

  private clearDevelopmentAreaSelection(row: GasPlantCapacityRow): void {
    row.developmentAreaCards = [];
    row.developmentArea = '';
  }

  validateCapacity(row: GasPlantCapacityRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    const value = parseFloat(target.value);
    row.capacity = isNaN(value) ? null : value;
    this.markAsEdited(row);
  }

  openFloatingFilter(event: MouseEvent, field: string): void {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 110 + window.scrollY,
      left: rect.left - 10 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt =>
        this.getFilterOptionLabel(String(opt)).toLowerCase().includes(search)
      );
      return filtered;
    }
    return [];
  }

  getFilterOptionLabel(option: string): string {
    return this.isBlankFilterValue(option) ? '(Blank)' : option;
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'date-range' | 'search' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      const normalizedValues = (this.multiFilters[field] || [])
        .map((value) => this.normalizeFilterValue(value))
        .filter((value) => value !== '');

      if (normalizedValues.length === 1) {
        this.currentFilters[field] = normalizedValues[0];
      } else if (normalizedValues.length > 1) {
        this.currentFilters[field] = normalizedValues.join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.reloadFilterOptions();
  }

  applyDateRangeFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearDateRangeFilter(): void {
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  applyCapacitySearch(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearCapacitySearch(): void {
    this.capacitySearchFilter = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  async clearFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'date-range') {
      this.clearDateRangeFilter();
      return;
    }

    if (filterType === 'search') {
      this.clearCapacitySearch();
      return;
    }

    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  hasAnyFilterApplied(): boolean {
    const hasCurrentFilter = Object.values(this.currentFilters).some(value => value !== null);
    const hasDateFilter = this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    const hasCapacityFilter = this.capacitySearchFilter !== null && this.capacitySearchFilter !== '';
    return hasCurrentFilter || hasDateFilter || hasCapacityFilter;
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.capacitySearchFilter = null;

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: GasPlantCapacityRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'GasPlantCapacitiesData.xlsx',
      'GasPlantCapacities'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.gasPlantService.exportAllGasPlantCapacitiesData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  isLockedColumn(field: string): boolean {
    const column = this.columns.find(col => col.field === field);
    return column?.locked || false;
  }

  isFilterableColumn(field: string): boolean {
    const column = this.columns.find(col => col.field === field);
    return column?.filterable !== false;
  }

  isFilterActive(field: string): boolean {
    const filterType = this.getFilterType(field);
    
    if (filterType === 'multi-select') {
      return (this.multiFilters[field] || []).length > 0;
    } else if (filterType === 'date-range') {
      return this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    } else if (filterType === 'search') {
      return this.capacitySearchFilter !== null && this.capacitySearchFilter !== '';
    }
    
    return false;
  }

  generateRowId(): string { 
    return `row_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; 
  }
}
